import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Typography, 
  CircularProgress, 
  Paper, 
  Grid, 
  Chip,
  Box,
  Divider,
  Select,
  MenuItem,
  Button,
  Link as MuiLink,
  Menu,
  MenuItem as MenuItemComponent
} from '@mui/material';
import { Download } from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import { exportNavigator2026ToPDF, exportNavigator2026ToCSV } from '../utils/navigator2026Export';

interface ApplicationData {
  _id: string;
  formType: string;
  formTitle: string;
  values: any;
  rawValues: any;
  status: string;
  submittedAt: string;
}

const Navigator2026ApplicantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const response = await axiosInstance.get(`/api/applications/submissions/${id}`);
        setApplication(response.data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching the application.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id]);

  const handleStatusChange = async (newStatus: string) => {
    try {
      await axiosInstance.patch(`/api/applications/submissions/${id}/status`, {
        status: newStatus
      });
      if (application) {
        setApplication({ ...application, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportPDF = () => {
    if (application) {
      exportNavigator2026ToPDF(application);
    }
    handleExportMenuClose();
  };

  const handleExportCSV = () => {
    if (application) {
      exportNavigator2026ToCSV(application);
    }
    handleExportMenuClose();
  };

  // Helper to extract YouTube video ID
  const getYouTubeId = (url: string): string | null => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Helper to check if URL is Instagram
  const isInstagramUrl = (url: string): boolean => {
    return url?.includes('instagram.com');
  };

  // Helper to render media embed or link
  const renderMediaLink = (url: string, label: string) => {
    if (!url) return null;

    const youtubeId = getYouTubeId(url);
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          {label}
        </Typography>
        
        {youtubeId ? (
          // YouTube embed
          <Box sx={{ 
            position: 'relative', 
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            maxWidth: '100%',
            backgroundColor: '#000',
            borderRadius: 1
          }}>
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={label}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        ) : isInstagramUrl(url) ? (
          // Instagram embed
          <Box>
            <MuiLink 
              href={url} 
              target="_blank" 
              rel="noopener"
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#E1306C',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <Typography component="span">View on Instagram →</Typography>
            </MuiLink>
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {url}
            </Typography>
          </Box>
        ) : (
          // Regular link
          <MuiLink href={url} target="_blank" rel="noopener">
            {url}
          </MuiLink>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!application) {
    return <Typography>No application data found.</Typography>;
  }

  const v = application.values;
  
  // Helper function to find value by key prefix
  const findValue = (prefix: string) => {
    const key = Object.keys(v).find(k => k.startsWith(prefix));
    return key ? v[key] : null;
  };
  
  const applicantName = `${findValue('firstName') || ''} ${findValue('lastName') || ''}`.trim();

  return (
    <Paper elevation={3} sx={{ padding: 4, marginTop: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Navigator 2026 Application
            </Typography>
            <Typography variant="h3" color="primary" gutterBottom>
              {applicantName}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportMenuOpen}
          >
            Export
          </Button>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleExportMenuClose}
        >
          <MenuItemComponent onClick={handleExportPDF}>Export as PDF</MenuItemComponent>
          <MenuItemComponent onClick={handleExportCSV}>Export as CSV</MenuItemComponent>
        </Menu>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Submitted: {new Date(application.submittedAt).toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold">Status:</Typography>
            <Select
              value={application.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              size="small"
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Contact Information */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contact Information
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography><strong>Email:</strong> {findValue('email')}</Typography>
            <Typography><strong>Phone:</strong> {findValue('phone')}</Typography>
            <Typography><strong>Address:</strong></Typography>
            <Typography sx={{ pl: 2 }}>
              {findValue('streetAddress1')}<br />
              {findValue('streetAddress2') && <>{findValue('streetAddress2')}<br /></>}
              {findValue('city')}, {findValue('county')}<br />
              {findValue('postcode')}
            </Typography>
          </Box>
        </Grid>

        {/* Online Presence */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Online Presence
          </Typography>
          <Box sx={{ pl: 2 }}>
            {findValue('field-1766062318797') && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">Website/Social Link 1:</Typography>
                <MuiLink href={findValue('field-1766062318797')} target="_blank" rel="noopener">
                  {findValue('field-1766062318797')}
                </MuiLink>
              </Box>
            )}
            {findValue('field-1766062356449') && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">Website/Social Link 2:</Typography>
                <MuiLink href={findValue('field-1766062356449')} target="_blank" rel="noopener">
                  {findValue('field-1766062356449')}
                </MuiLink>
              </Box>
            )}
            {findValue('field-1766062414839') && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">Website/Social Link 3:</Typography>
                <MuiLink href={findValue('field-1766062414839')} target="_blank" rel="noopener">
                  {findValue('field-1766062414839')}
                </MuiLink>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Work Samples with Embeds */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Work Samples
          </Typography>
          <Box sx={{ pl: 2 }}>
            {renderMediaLink(findValue('field-1766062474185'), 'Work Sample 1 (Live Video)')}
            {renderMediaLink(findValue('field-1766062504736'), 'Work Sample 2')}
            {renderMediaLink(findValue('field-1766062529548'), 'Work Sample 3')}
          </Box>
        </Grid>

        {/* About the Applicant */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            About You
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#1e2a38' }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {findValue('field-1766062588584')}
            </Typography>
          </Paper>
        </Grid>

        {/* Why Navigator */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Why Navigator? (Value to Career)
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#1e2a38' }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {findValue('field-1766062755596')}
            </Typography>
          </Paper>
        </Grid>

        {/* Residency Plans */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Residency Plans
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: '#1e2a38' }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {findValue('field-1766062851194')}
            </Typography>
          </Paper>
        </Grid>

        {/* Engagement Preferences */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Engagement Preferences
          </Typography>
          {findValue('field-1766062954076') && Array.isArray(findValue('field-1766062954076')) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {findValue('field-1766062954076').map((engagement: string, index: number) => (
                <Chip key={index} label={engagement} color="primary" />
              ))}
            </Box>
          )}
          {findValue('field-1766063184249') && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">Other engagement ideas:</Typography>
              <Typography>{findValue('field-1766063184249')}</Typography>
            </Box>
          )}
        </Grid>

        {/* Location Preferences */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Location & Time Period Preferences
          </Typography>
          {findValue('field-1766063219475') && 
           Array.isArray(findValue('field-1766063219475')) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {findValue('field-1766063219475').map((location: string, index: number) => (
                <Chip key={index} label={location} color="secondary" size="medium" />
              ))}
            </Box>
          )}
          {findValue('field-1766063454068') && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" fontWeight="bold">Interest in Local Area:</Typography>
              <Paper sx={{ p: 2, backgroundColor: '#1e2a38', mt: 1 }}>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {findValue('field-1766063454068')}
                </Typography>
              </Paper>
            </Box>
          )}
        </Grid>

        {/* Additional Needs */}
        {findValue('field-1766063456747') && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Additional Needs / Caring Responsibilities
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: '#1e2a38' }}>
              <Typography sx={{ whiteSpace: 'pre-line' }}>
                {findValue('field-1766063456747')}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => window.history.back()}>
          Back to List
        </Button>
        <Button variant="contained" color="primary">
          Email Applicant
        </Button>
      </Box>
    </Paper>
  );
};

export default Navigator2026ApplicantDetailPage;